import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

export default function LineItemsEditor({ items, setItems, discount, setDiscount, deposit, setDeposit, services }) {
  const update = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const pickService = (i, id) => {
    const s = (services || []).find((x) => x.id === id);
    if (!s) return;
    setItems(items.map((it, idx) => (idx === i ? { ...it, description: s.name, unit_price: s.price != null ? s.price : (it.unit_price || 0) } : it)));
  };
  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const total = subtotal - (Number(discount) || 0);
  const hasCatalog = Array.isArray(services) && services.length > 0;

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            data-testid={`line-item-${i}`}
            className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:p-0 sm:border-0 sm:bg-transparent sm:rounded-none"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              {/* Prestation / Description */}
              <div className="sm:flex-1 space-y-1">
                <Label className="text-[11px] text-slate-500 sm:hidden">Prestation / Description</Label>
                {hasCatalog && (
                  <Select value="" onValueChange={(v) => pickService(i, v)}>
                    <SelectTrigger data-testid={`item-service-${i}`} className="bg-white border-slate-200 h-9 mb-1.5">
                      <SelectValue placeholder="Choisir une prestation du catalogue…" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-64">
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.price != null ? ` — ${eur(s.price)}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  placeholder="Description de la prestation"
                  value={it.description}
                  onChange={(e) => update(i, "description", e.target.value)}
                  data-testid={`item-desc-${i}`}
                  className="bg-white sm:bg-slate-50 border-slate-200 w-full"
                />
              </div>

              {/* Quantité + Prix unitaire */}
              <div className="flex gap-2">
                <div className="w-20 sm:w-16 space-y-1">
                  <Label className="text-[11px] text-slate-500 sm:hidden">Quantité</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder="Qté"
                    value={it.quantity}
                    onChange={(e) => update(i, "quantity", e.target.value)}
                    data-testid={`item-qty-${i}`}
                    className="bg-white sm:bg-slate-50 border-slate-200 w-full"
                  />
                </div>
                <div className="flex-1 sm:flex-none sm:w-24 space-y-1">
                  <Label className="text-[11px] text-slate-500 sm:hidden">Prix unitaire (€)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder="Prix €"
                    value={it.unit_price}
                    onChange={(e) => update(i, "unit_price", e.target.value)}
                    data-testid={`item-price-${i}`}
                    className="bg-white sm:bg-slate-50 border-slate-200 w-full"
                  />
                </div>
              </div>

              {/* Total ligne + suppression */}
              <div className="flex items-center justify-between gap-2 sm:justify-end">
                <div className="space-y-1 text-right">
                  <Label className="text-[11px] text-slate-500 sm:hidden">Total ligne</Label>
                  <div
                    data-testid={`item-total-${i}`}
                    className="w-24 text-right font-mono text-sm font-semibold text-slate-900 sm:font-normal sm:text-slate-700 sm:pt-2"
                  >
                    {eur((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`item-remove-${i}`}
                  className="text-red-500 hover:text-red-700 shrink-0 sm:pt-2.5"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="btn-add-item" className="border-slate-200">
        <Plus className="w-4 h-4 mr-1.5" /> Ajouter une ligne
      </Button>

      <div className="flex flex-col items-end gap-2 pt-3 border-t border-slate-200 text-sm">
        <div className="flex items-center gap-3"><span className="text-slate-500">Sous-total</span><span className="font-mono w-28 text-right">{eur(subtotal)}</span></div>
        <div className="flex items-center gap-3"><span className="text-slate-500">Remise (€)</span><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="input-discount" className="w-28 h-8 bg-slate-50 border-slate-200 text-right" /></div>
        {setDeposit && <div className="flex items-center gap-3"><span className="text-slate-500">Acompte (€)</span><Input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} data-testid="input-deposit" className="w-28 h-8 bg-slate-50 border-slate-200 text-right" /></div>}
        <div className="flex items-center gap-3 text-base font-semibold"><span className="text-slate-900">TOTAL</span><span className="font-mono w-28 text-right text-emerald-600">{eur(total)}</span></div>
      </div>
    </div>
  );
}
